import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { toast } from 'react-toastify';
import { Box, Typography } from '@mui/material';

import { FBoxContainer, FCheckbox, FHButton, FHSignatureDialog, FHTextarea } from '../../../base';
import { clientAssessmentActions } from '../../../../store';
import { AssessmentNoteStatus, Roles, Themes } from '../../../../constants';
import { FORTA_BLACK, FORTA_BLUE, FORTA_ERROR } from '../../../../utils/colors';
import { ASSESSMENT_NOTE_MIN_LENGTH } from './constants';
import { getUtcOffset } from '../../../../utils/common';
import {
  areUserRolesIn,
  getAllInvalidFieldNames,
  history,
  validateSchema,
} from '../../../../helpers';
import { assessmentFormSchema } from './validationSchema';

import type { IAssessmentActivity, IClientAssessment } from '../../../../utils/types';
import type { RootState } from '../../../../store';
import type { ChangeEvent } from 'react';

interface IClientAssessmentFormProps {
  clientAssessment?: IClientAssessment;
  isReadOnlyView: boolean;
}

const initialAssessmentActivityState = {
  is_assessment_administered: false,
  is_findings_of_assessment_discussed: false,
  is_recommendations_based_on_assessment_discussed: false,
  is_past_data_analyzed_non_face_to_face: false,
  is_assessment_scored_or_interpreted: false,
  is_report_or_treatment_plan_prepared: false,
};

export function ClientAssessmentForm({
  clientAssessment,
  isReadOnlyView,
}: IClientAssessmentFormProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);

  const { loading: clientAssessmentLoading } = useSelector(
    (state: RootState) => state.clientAssessment,
  );

  const isEditable =
    !clientAssessmentLoading &&
    areUserRolesIn(user?.roles, [Roles.Bcba, Roles.AssistantAssessor]) &&
    user?.email === clientAssessment?.provider.email &&
    Boolean(clientAssessment?.id) &&
    clientAssessment?.status !== AssessmentNoteStatus.Completed;

  const [assessmentActivity, setAssessmentActivity] = useState<IAssessmentActivity>(
    initialAssessmentActivityState,
  );
  const [note, setNote] = useState<string | null>('');
  const [openSignatureDialog, setOpenSignatureDialog] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});

  // Fill data from response
  useEffect(() => {
    if (!clientAssessment) {
      setAssessmentActivity(initialAssessmentActivityState);
      setNote(null);
      return;
    }

    const { activity_conducted, notes } = clientAssessment;
    if (activity_conducted) {
      // handle null values
      const activity: IAssessmentActivity = {
        is_assessment_administered: activity_conducted.is_assessment_administered ?? false,
        is_findings_of_assessment_discussed:
          activity_conducted.is_findings_of_assessment_discussed ?? false,
        is_recommendations_based_on_assessment_discussed:
          activity_conducted.is_recommendations_based_on_assessment_discussed ?? false,
        is_past_data_analyzed_non_face_to_face:
          activity_conducted.is_past_data_analyzed_non_face_to_face ?? false,
        is_assessment_scored_or_interpreted:
          activity_conducted.is_assessment_scored_or_interpreted ?? false,
        is_report_or_treatment_plan_prepared:
          activity_conducted.is_report_or_treatment_plan_prepared ?? false,
      };
      setAssessmentActivity(activity);
    }
    setNote((prevNote) => (!notes ? (prevNote ?? null) : notes));
  }, [clientAssessment]);

  const handleAssessmentActivityUpdate = (field: string, checked: boolean) => {
    setAssessmentActivity((prev) => ({
      ...prev,
      [field]: checked,
    }));
    const data = {
      ...clientAssessment,
      notes: note && note.length >= ASSESSMENT_NOTE_MIN_LENGTH ? note : null,
      activity_conducted: { ...assessmentActivity, [field]: checked },
    };
    const isValid = validateSchema(assessmentFormSchema, 'activity_conducted', {
      ...clientAssessment,
      activity_conducted: { ...assessmentActivity, [field]: checked },
    });
    setErrors({ ...errors, activity_conducted: !isValid });
    dispatch(
      clientAssessmentActions.updateAssessmentNote({
        getAccessTokenSilently,
        data,
        assessmentNoteId: clientAssessment?.id,
      }),
    );
  };

  const handleNoteUpdate = (value: string | null) => {
    setNote(value);
    const data = {
      ...clientAssessment,
      activity_conducted: assessmentActivity,
      notes: value,
    };
    if (value === null || value.trim().length > ASSESSMENT_NOTE_MIN_LENGTH) {
      dispatch(
        clientAssessmentActions.updateAssessmentNote({
          getAccessTokenSilently,
          data,
          assessmentNoteId: clientAssessment?.id,
        }),
      );
    }
  };

  const handleConclude = () => {
    const data = {
      activity_conducted: assessmentActivity,
      notes: note,
    };
    const errors: { [key: string]: boolean } = getAllInvalidFieldNames(assessmentFormSchema, data);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      if (errors.activity_conducted) {
        toast.error('Please select at least one activity');
        return;
      }
      if (errors.notes) {
        toast.error('Please complete notes');
        return;
      }
    } else {
      setOpenSignatureDialog(true);
    }
  };

  const onSubmit = async (dataUrl: string) => {
    if (!dataUrl || !clientAssessment) return;
    const data = {
      signature: dataUrl,
      date_signed: moment(new Date()),
      utc_offset_date_signed: getUtcOffset(moment(new Date())),
    };
    const response = await dispatch(
      clientAssessmentActions.concludeAssessmentNote({
        assessmentNoteId: clientAssessment?.id,
        data,
        getAccessTokenSilently,
      }),
    );
    setOpenSignatureDialog(false);
    if (!response || response.error) {
      return;
    }
    history.navigate('/dashboard');
  };

  const SignatureMessage = () => (
    <Box>
      <Typography variant="body1" align="left" color={FORTA_BLACK} fontSize={12}>
        By signing and submitting this record, I hereby attest that (i) I am the rendering or
        responsible clinician named in the assessment, treatment plan, document and/or other records
        contained and referenced herein; (ii) the aforementioned assessment, treatment plan,
        documents and/or other records accurately and completely reflect my assessment based on
        industry standards; (ii) I have thoroughly reviewed all of the client&apos;s assessments,
        treatment plans, documents and/or other records in making this determination; and (iii) I
        have discussed all applicable findings, outcomes, issues or discrepancies with the client
        and/or client&apos;s legal guardian, as appropriate.
      </Typography>
      <Typography variant="body1" align="left" color={FORTA_BLACK} fontSize={12}>
        I understand that any falsification, omission, or concealment of facts may subject me to
        administrative, civil, or criminal liability as well as termination of my employment with
        the company.
      </Typography>
    </Box>
  );

  return (
    <>
      <FBoxContainer sx={{ mt: 3, flexGrow: 0, boxShadow: 'none' }}>
        <Box>
          <Box sx={{ display: 'flex' }}>
            <Typography fontWeight={600} textAlign="left" fontSize="18px">
              Activities conducted
            </Typography>
            {errors?.activity_conducted && (
              <Typography ml={3} color={FORTA_ERROR}>
                Select at least one
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', mt: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                pr: 2,
                borderRight: `1px solid ${FORTA_BLUE.light2}`,
              }}
            >
              <FCheckbox
                data-testid={`administeredAssessmentCheckbox`}
                label={'Administered assessment'}
                iconStyles={{ boxShadow: 'none' }}
                checked={assessmentActivity.is_assessment_administered}
                disabled={!isEditable}
                onChange={(e) => {
                  handleAssessmentActivityUpdate('is_assessment_administered', e.target.checked);
                }}
              />
              <FCheckbox
                data-testid={`discussedFindingsOfAssessmentCheckbox`}
                label={'Discussed findings of assessment'}
                sx={{ my: 3 }}
                iconStyles={{ boxShadow: 'none' }}
                checked={assessmentActivity.is_findings_of_assessment_discussed}
                disabled={!isEditable}
                onChange={(e) => {
                  handleAssessmentActivityUpdate(
                    'is_findings_of_assessment_discussed',
                    e.target.checked,
                  );
                }}
              />
              <FCheckbox
                data-testid={`discussedRecommendationsBasedOnAssessmentCheckbox`}
                label={'Discussed recommendations based on assessment'}
                iconStyles={{ boxShadow: 'none' }}
                checked={assessmentActivity.is_recommendations_based_on_assessment_discussed}
                disabled={!isEditable}
                onChange={(e) => {
                  handleAssessmentActivityUpdate(
                    'is_recommendations_based_on_assessment_discussed',
                    e.target.checked,
                  );
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', pl: 3 }}>
              <FCheckbox
                data-testid="nonFaceToFaceAnalyzingPastDataCheckbox"
                label={'Non face-to-face analyzing past data'}
                iconStyles={{ boxShadow: 'none' }}
                checked={assessmentActivity.is_past_data_analyzed_non_face_to_face}
                disabled={!isEditable}
                onChange={(e) => {
                  handleAssessmentActivityUpdate(
                    'is_past_data_analyzed_non_face_to_face',
                    e.target.checked,
                  );
                }}
              />
              <FCheckbox
                data-testid={`scoringOrInterpretingAssessmentCheckbox`}
                label={'Scoring or interpreting assessment'}
                sx={{ my: 3 }}
                iconStyles={{ boxShadow: 'none' }}
                checked={assessmentActivity.is_assessment_scored_or_interpreted}
                disabled={!isEditable}
                onChange={(e) => {
                  handleAssessmentActivityUpdate(
                    'is_assessment_scored_or_interpreted',
                    e.target.checked,
                  );
                }}
              />
              <FCheckbox
                data-testid={`preparingTheReportTreatmentPlanCheckbox`}
                label={'Preparing the report/treatment plan'}
                iconStyles={{ boxShadow: 'none' }}
                checked={assessmentActivity.is_report_or_treatment_plan_prepared}
                disabled={!isEditable}
                onChange={(e) => {
                  handleAssessmentActivityUpdate(
                    'is_report_or_treatment_plan_prepared',
                    e.target.checked,
                  );
                }}
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ mt: 3 }}>
          <Typography fontWeight={600} textAlign="left" fontSize="18px" mb={1}>
            Notes on assessments completed, findings, and recommendations
          </Typography>
          {isReadOnlyView ? (
            <Typography textAlign={'left'} mt={3}>
              {clientAssessment?.notes}
            </Typography>
          ) : (
            <FHTextarea
              data-testid="clientAssessmentNoteTextField"
              colorTheme={Themes.Primary}
              maxRows={8}
              sx={{ boxShadow: 'none' }}
              placeholder="Add any notes on the assessments completed, findings, and recommendations"
              disabled={!isEditable}
              expandable={isEditable}
              value={note || ''}
              onEdit={(e: ChangeEvent<HTMLTextAreaElement>) => {
                setNote(e?.target.value || null);
                const isValid = validateSchema(assessmentFormSchema, 'notes', {
                  ...clientAssessment,
                  notes: e?.target.value,
                });
                if (!isValid) {
                  setErrors({
                    ...errors,
                    notes: true,
                  });
                } else {
                  setErrors({
                    ...errors,
                    notes: false,
                  });
                }
              }}
              onBlur={(e) => {
                handleNoteUpdate(e?.target.value?.trim() || null);
              }}
              onCloseExpandDialog={() => {
                handleNoteUpdate(note || null);
              }}
              error={errors?.notes}
              helperText={
                <Typography color={errors?.notes ? FORTA_ERROR : FORTA_BLUE.primary} fontSize={14}>
                  Minimum 120 characters
                </Typography>
              }
            />
          )}
        </Box>
      </FBoxContainer>
      {!isReadOnlyView && (
        <>
          <Box mt={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <FHButton
              onClick={handleConclude}
              disabled={!isEditable}
              data-testid="assessmentNoteConclude"
            >
              Conclude
            </FHButton>
          </Box>
          <FHSignatureDialog
            headerText={'Signature'}
            cancelText="Clear signature"
            open={openSignatureDialog}
            onClose={() => setOpenSignatureDialog(false)}
            onSign={(dataUrl: string) => onSubmit(dataUrl)}
            contentRenderer={SignatureMessage}
          />
        </>
      )}
    </>
  );
}
